import { ReactNode } from 'react';
import { useParams } from 'react-router';
import DateHelpers from '../../helpers/dateHelpers';
import { CircleUser, MapPin } from 'lucide-react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { useInspectionCached } from '../../hooks/useInspectionCached';
import { Tooltip } from '../atoms/Tooltip';

interface ScreenHeadingProps {
  /** THE screen title: 24px bold brand navy - one size, one colour, everywhere. */
  title?: string;
  /** Section path, rendered as the small tracked OVERLINE above the title
   *  ("UNDERCARRIAGE · SHOES") - insite's header language. Auto-prefixed with
   *  Undercarriage on side-scoped routes. */
  eyebrow?: string;
  /** Optional icon rendered beside the title (insite pairs titles with icons). */
  icon?: ReactNode;
  /** Side tag rendered as the navy overline pill. Auto-derived from :sideName. */
  tag?: string;
  /** Part code line (codes stay uppercase - they are identifiers). */
  partNumber?: string;
  tooltipKey?: string;
  /** Stick to the top of the scroll area (long table-of-contents screens). */
  sticky?: boolean;
  /** Suppress the automatic machine/customer context (non-inspection screens). */
  hideContext?: boolean;
  /** Identity screens: full timestamp instead of the short dated-inspection line. */
  fullDate?: boolean;
}

/** Subtle brand plexus motif (nodes + lines in brandLight), self-contained. */
const PLEXUS =
  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='420' height='140' viewBox='0 0 420 140'><g stroke='%23BAD2ED' stroke-width='1' fill='none'><path d='M30 110 L110 40 L200 90 L290 25 L370 95 L410 50'/><path d='M110 40 L180 15 L290 25'/><path d='M200 90 L250 130 L340 115 L370 95'/></g><g fill='%23BAD2ED'><circle cx='30' cy='110' r='4'/><circle cx='110' cy='40' r='5'/><circle cx='180' cy='15' r='3'/><circle cx='200' cy='90' r='4'/><circle cx='250' cy='130' r='3'/><circle cx='290' cy='25' r='5'/><circle cx='340' cy='115' r='4'/><circle cx='370' cy='95' r='5'/><circle cx='410' cy='50' r='3'/></g></svg>\")";

/**
 * THE standard screen header (there is exactly one). Anatomy, top to bottom:
 *   overline path (small tracked caps) · title + icon (+ side pill right)
 *   machine line - asset name · model · type
 *   customer line - customer · site
 *   dated-inspection line (full timestamp on identity screens) · part number
 *   brand-gradient rule
 * Context populates automatically from the route's :externalId via the cached
 * inspection query. Raw h1-h3 in pages are banned by the colourRatchet.
 */
export const ScreenHeading = ({
  title,
  eyebrow,
  icon,
  tag,
  partNumber,
  tooltipKey,
  sticky = false,
  hideContext = false,
  fullDate = false,
}: ScreenHeadingProps) => {
  const { externalId, sideName } = useParams();
  const { inspection, isLoadingInspection } = useInspectionCached(
    hideContext ? undefined : externalId,
  );
  const effectiveTag = tag ?? sideName?.replace(/-side$/, '');

  const asset = inspection?.asset;
  const machineName =
    asset?.name ?? inspection?.mineLocation?.mineLocation?.name;
  const machineMeta = asset
    ? [asset.model?.name, asset.model?.assetType].filter(Boolean).join(' · ')
    : undefined;
  const customerName = asset?.customerName;
  const siteName = asset?.siteName;
  const contextExpected = !hideContext && !!externalId;
  const contextLoading = contextExpected && !inspection && isLoadingInspection;

  const overline = eyebrow && sideName ? `Undercarriage · ${eyebrow}` : eyebrow;

  // Side-scoped titles name their side ("Left side frame", "Right drive lug")
  // unless the title already does (the side ToCs: "Undercarriage left side").
  const side = sideName?.replace(/-side$/, '');
  const displayTitle =
    title && side && !/\b(left|right)\b/i.test(title)
      ? `${side.charAt(0).toUpperCase()}${side.slice(1)} ${title.charAt(0).toLowerCase()}${title.slice(1)}`
      : title;

  const rawDate = inspection?.inspection?.inspectionDate;
  // Standard timestamp form, Sydney common time for synced inspections. The
  // raw browser-local rendering here disagreed with the cards by an hour
  // during DST and used the retired weekday/seconds/AM-PM format.
  const dateText = rawDate
    ? fullDate
      ? DateHelpers.displayShortTimestamp({
          timestamp: new Date(rawDate),
          convertToCommonTime: inspection?.inspection?.isSynced,
        })
      : `${DateHelpers.displayShortTimestamp({
          timestamp: new Date(rawDate),
          convertToCommonTime: inspection?.inspection?.isSynced,
          includeTime: false,
        })} inspection`
    : undefined;

  return (
    <div
      className={`relative -mx-4 mb-4 overflow-hidden bg-white px-4 pt-2 pb-3 ${
        sticky ? 'sticky -top-4 z-10' : ''
      }`}
    >
      {/* Brand plexus motif, right-anchored behind the content */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-y-0 right-0 w-1/2 bg-right bg-no-repeat opacity-40"
        style={{ backgroundImage: PLEXUS }}
      />
      <div className="relative">
        {overline && (
          <div
            className="text-[11px] font-bold tracking-widest text-inkMuted uppercase"
            data-testid="heading-overline"
          >
            {overline}
          </div>
        )}
        <div className="mb-1 flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            {icon && <span className="shrink-0 text-primaryColor">{icon}</span>}
            <h1 className="text-2xl leading-tight font-bold text-primaryColor">
              {displayTitle}
            </h1>
            {tooltipKey && <Tooltip tooltipKey={tooltipKey} />}
          </div>
          {effectiveTag && (
            <div className="flex shrink-0 items-center justify-center self-center rounded-full bg-primaryColor px-3 py-1">
              <span className="text-sm leading-none font-bold tracking-wide text-white uppercase">
                {effectiveTag}
              </span>
            </div>
          )}
        </div>
        {contextLoading && (
          <div aria-busy="true">
            <Skeleton width={260} height={14} />
            <Skeleton width={200} height={14} />
            <Skeleton width={140} height={14} />
          </div>
        )}
        {!hideContext && machineName && (
          <div className="truncate text-base" data-testid="heading-machine">
            <span className="font-bold text-ink">{machineName}</span>
            {machineMeta && (
              <span className="text-inkMuted"> · {machineMeta}</span>
            )}
          </div>
        )}
        {!hideContext && (customerName || siteName) && (
          <div
            className="flex items-center gap-1 truncate text-sm text-inkMuted"
            data-testid="heading-customer"
          >
            <CircleUser className="size-4 shrink-0" /> {customerName}
            <span className="mx-1">·</span>
            <MapPin className="size-4 shrink-0" /> {siteName}
          </div>
        )}
        {dateText && (
          <div
            className="mt-0.5 text-sm text-inkMuted"
            data-testid="heading-specific"
          >
            {dateText}
          </div>
        )}
        {partNumber && partNumber.length > 0 && (
          <div className="mt-1 text-base font-bold text-ink">
            Part #{partNumber.toUpperCase()}
          </div>
        )}
      </div>
      {/* The official Bradken gradient (BK28: #00305F -> #005596 @50%) accent rule */}
      <div className="absolute right-0 bottom-0 left-0 h-px bg-line" />
    </div>
  );
};
